import React, { Fragment, useEffect, useState } from "react";
import { Dialog, DialogPanel, Transition, TransitionChild } from "@headlessui/react";
import { AiFillCloseCircle } from "react-icons/ai";
import { MdDelete } from "react-icons/md";

import { userImageProfile } from "../api";
import ToastMessage from "./ToastMessage";

const MAX_FILE_SIZE = 5 * 1024 * 1024;

const ProfilePicture = ({ pictureOpen, pictureClose }) => {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!file) {
      setPreviewUrl("");
      return;
    }

    const url = URL.createObjectURL(file);
    setPreviewUrl(url);

    return () => URL.revokeObjectURL(url);
  }, [file]);

  const clearFile = () => {
    setFile(null);
    setError("");
  };

  const handleFile = (e) => {
    const selectedFile = e.target.files?.[0];
    if (!selectedFile) return;

    const fileTypes = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

    if (!fileTypes.includes(selectedFile.type)) {
      setFile(null);
      setError("File needs to be a JPG, PNG or WEBP image.");
      e.target.value = "";
      return;
    }

    if (selectedFile.size > MAX_FILE_SIZE) {
      setFile(null);
      setError("Image must be 5MB or smaller.");
      e.target.value = "";
      return;
    }

    setFile(selectedFile);
    setError("");
    e.target.value = "";
  };

  const handleClose = () => {
    setFile(null);
    setError("");
    pictureClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file || isSubmitting) return;

    try {
      setIsSubmitting(true);
      setError("");

      const formData = new FormData();
      formData.append("content", file);

      const res = await userImageProfile(formData);

      setFile(null);
      pictureClose();

      ToastMessage(res?.data?.message || "Your profile picture has been updated successfully!");
    } catch (error) {
      console.error("Profile picture update error:", error);

      const message = error?.response?.data?.message || "Unable to update your profile picture.";

      setError(message);
      ToastMessage(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Transition appear show={pictureOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <TransitionChild as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
          <div className="fixed inset-0 bg-black/25" />
        </TransitionChild>

        <div className="fixed inset-0">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <TransitionChild as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 scale-95" enterTo="opacity-100 scale-100" leave="ease-out duration-300" leaveFrom="opacity-100 scale-100" leaveTo="opacity-0 scale-95">
              <DialogPanel className="relative w-full max-w-md rounded-3xl bg-white p-6 shadow-2xl transition-all">
                {/* CLOSE */}

                <button type="button" onClick={handleClose} disabled={isSubmitting} className="absolute right-4 top-4 rounded-full p-2 text-gray-400 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50">
                  <AiFillCloseCircle size={22} />
                </button>

                {/* TITLE */}

                <div className="mb-6 text-center">
                  <h2 className="text-xl font-semibold text-gray-900">Update profile picture</h2>
                  <p className="mt-1 text-sm text-gray-500">JPG, PNG or WEBP • Max 5MB</p>
                </div>

                <form onSubmit={handleSubmit} className="flex flex-col items-center gap-6">
                  {/* AVATAR — 1:1 */}

                  <div className="group relative">
                    <div className="h-40 w-40 overflow-hidden rounded-full shadow-lg ring-4 ring-purple-500/20">
                      {previewUrl ? (
                        <img src={previewUrl} alt="Profile picture preview" className="h-full w-full object-cover" />
                      ) : (
                        <div className="flex h-full w-full items-center justify-center rounded-full bg-gray-100 text-gray-400">
                          <span className="text-sm font-medium">No image</span>
                        </div>
                      )}
                    </div>

                    {/* CHANGE OVERLAY */}

                    <label className="absolute inset-0 flex cursor-pointer items-center justify-center rounded-full bg-black/40 opacity-0 transition group-hover:opacity-100">
                      <span className="rounded-lg bg-white px-4 py-2 text-sm font-medium text-gray-800">{file ? "Change" : "Choose"}</span>
                      <input type="file" className="hidden" accept="image/jpeg,image/png,image/webp" onChange={handleFile} disabled={isSubmitting} />
                    </label>

                    {/* REMOVE */}

                    {file && (
                      <button type="button" onClick={clearFile} disabled={isSubmitting} className="absolute -bottom-2 right-2 rounded-full bg-red-500 p-2 text-white shadow transition hover:bg-red-600 disabled:cursor-not-allowed disabled:opacity-50">
                        <MdDelete size={16} />
                      </button>
                    )}
                  </div>

                  {/* ERROR */}

                  {error && <p className="text-sm font-medium text-red-500">{error}</p>}

                  {/* ACTION */}

                  <div className="flex w-full gap-3 pt-4">
                    <button type="submit" disabled={!file || isSubmitting} className="flex-1 cursor-pointer rounded-xl bg-[#6469ff] py-3 text-sm font-semibold text-white transition hover:bg-[#555bea] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50">
                      {isSubmitting ? "Saving..." : "Save"}
                    </button>
                  </div>
                </form>
              </DialogPanel>
            </TransitionChild>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
};

export default ProfilePicture;